import EditContentBankResourcesDialog from 'components/CreateAssignment/EditContentBankResourcesDialog';
import PushRubricDialog from 'components/InstructorAssignments/PushRubricDialog';
import UploadPreviewSubmissionsDialog from 'components/InstructorViewAssignment/UploadPreviewSubmissionsDialog';
import UploadSubmissionsDialog from 'components/InstructorViewAssignment/UploadSubmissionsDialog';
import ViewGradingSettingsDialog from 'components/InstructorViewAssignment/ViewGradingSettingsDialog';
import { features } from 'config/features';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import ErrorPage from 'pages/Error/404';
import Desktop from 'pages/InstructorViewAssignment/desktop';
import Mobile from 'pages/InstructorViewAssignment/mobile';
import { PageContext } from 'pages/InstructorViewAssignment/PageProvider';
import Tablet from 'pages/InstructorViewAssignment/tablet';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import { Button, Loading, Stack, Text } from 'shell/components';
import { EXAMPLE_ASSIGNMENT_ID } from 'utils/constants';
import { compareCanvasRubrics, isAssignmentValid, resolveFeatureFlags } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageLayout = () => {
    const { auth } = useAuth();
    const { isLoading, course, assignments, setAssignments, department, currentAssignmentId, currentAssignment } =
        useApp();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');
    const history = useHistory();
    const params = useParams();
    const { message, close } = useSnackbar();
    const { device } = useViewport();

    const [isFetchingLatestAssignment, setIsFetchingLatestAssignment] = useState(true);
    const [isDuplicating, setIsDuplicating] = useState(false);

    const {
        loading,
        viewContentBankResourcesDialog,
        setViewContentBankResourcesDialog,
        viewGradingSettingsDialog,
        setViewGradingSettingsDialog,
        uploadSubmissionsDialog,
        setUploadSubmissionsDialog,
        uploadPreviewSubmissionsDialog,
        setUploadPreviewSubmissionsDialog,
        isPreviewGenerating,
        isSubmissionsGenerating,
        attachPreviewFiles,
        pushRubricDialog,
        setPushRubricDialog,
        createSubmissions,
        importAPISubmissions,
        exportSubmissions,
        pushGrades,
        resetSubmissions,
    } = useContext(PageContext);

    const handleClickBack = () => {
        history.push('/assignments');
    };

    const handleClickDuplicate = async (assignment) => {
        setIsDuplicating(true);
        const timestamp = new Date().getTime();
        const duplicate = {
            ...omit(assignment, ['_id', 'api_assignment_id', 'api_data', 'is_rag_ready']),
            id: uuid(),
            owner_account_id: auth.id,
            created_on: timestamp,
            modified_on: timestamp,
            title: t('duplicate_title', 'Copy of {{title}}', { title: assignment.title }),
            is_draft: true,
            has_seen_preview: false,
            has_finished_preview: !features.enableFinetuning, // initialize as true when fine tuning is hidden
            preview_files: [],
        };
        await courseService.createCourseAssignment(course.id, duplicate);
        setAssignments((current) => [...current, duplicate]);
        message.success(
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <Text color={'inherit'} variant={'p1'}>
                    {t('message.duplicate-success', 'Your assignment was successfully duplicated.')}
                </Text>
                <Button
                    color={'neutral'}
                    size={'large'}
                    variant={'borderless'}
                    onClick={() => {
                        history.push(`/assignments/${duplicate.id}`);
                        close();
                    }}
                >
                    {t('common:controls.open', 'Open')}
                </Button>
            </Stack>
        );
        setIsDuplicating(false);
    };

    const handleClickUnpublish = (assignment) => {
        setDialog({
            open: true,
            message: t('dialog.unpublish-assignment.title', 'Unpublish assignment?'),
            description: t(
                'dialog.unpublish-assignment.description',
                'Are you sure you want to unpublish this assignment?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.unpublish', 'Unpublish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((a) => {
                        if (a.id !== assignment.id) return a;
                        return {
                            ...a,
                            modified_on: timestamp,
                            is_draft: true,
                        };
                    });
                });
                closeDialog();
                message.success(t('message.update-success', 'Your assignment was successfully updated.'));
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: true,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickPublish = (assignment) => {
        if (!isAssignmentValid(assignment)) {
            return setDialog({
                open: true,
                message: t('dialog.required-information-incomplete.title', 'Required Information Incomplete'),
                description: t(
                    'dialog.required-information-incomplete.description',
                    'Before you can publish this assignment, please edit it and add any missing required information.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        setDialog({
            open: true,
            message: t('dialog.publish-assignment.title', 'Publish assignment?'),
            description: t(
                'dialog.publish-assignment.description',
                'Are you sure you want to publish this assignment?'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.publish', 'Publish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((a) => {
                        if (a.id !== assignment.id) return a;
                        return {
                            ...a,
                            modified_on: timestamp,
                            is_draft: false,
                        };
                    });
                });
                closeDialog();
                message.success(t('message.update-success', 'Your assignment was successfully updated.'));
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: false,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const closePushRubricDialog = () => {
        setPushRubricDialog({ open: false });
    };

    const handleClickEdit = () => {
        history.push(`/assignments/${currentAssignment.id}/edit`);
    };

    const handleClickViewContentBank = () => {
        setViewContentBankResourcesDialog({ open: true });
    };

    const handleClickViewGradingSettings = () => {
        setViewGradingSettingsDialog({ open: true });
    };

    const handleClickViewSubmissions = () => {
        history.push(`/assignments/${currentAssignment.id}/submissions`);
    };

    const checkLMSRubric = async () => {
        const accessToken = localStorage.getItem('access_token');
        const APIAssignment = await apiService.getAssignment({
            course_id: currentAssignment.course_id,
            accessToken,
            assignment_id: currentAssignment.api_assignment_id,
        });
        const is_matching = compareCanvasRubrics(currentAssignment.rubric, APIAssignment.rubric);
        if (is_matching) {
            pushGrades();
        } else {
            setDialog({
                open: true,
                message: 'Rubrics Are Not Synced',
                description: (
                    <Stack spacing={4}>
                        <Text>
                            The rubric on your LMS doesn't match the one used to generate grades/feedback on
                            TimelyGrader. If you continue, only the overall final grade will be passed back for each
                            student.
                        </Text>

                        <Text>
                            To pass back the graded rubric, please cancel and resync the assignment. Note that you will
                            need to regrade the submissions.
                        </Text>
                    </Stack>
                ),
                note: '',
                variant: 'primary',
                actionButtonText: 'Continue',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    setDialog({ open: false });
                    return await resetSubmissions();
                },
                onCancel: () => setDialog({ open: false }),
                onClose: () => setDialog({ open: false }),
            });
        }
    };

    const handleSelect = ({ key }) => {
        if (key === 'csv') {
            exportSubmissions();
        }
        if (key === 'push-grades') {
            checkLMSRubric();
        }
        if (key === 'delete') {
            resetSubmissions();
        }
    };

    const props = {
        loading,
        assignment: currentAssignment,
        isDuplicating,
        isPreviewGenerating,
        isSubmissionsGenerating,
        onClickBack: handleClickBack,
        onClickDuplicate: handleClickDuplicate,
        onClickUnpublish: handleClickUnpublish,
        onClickPublish: handleClickPublish,
        onClickEdit: handleClickEdit,
        onClickViewContentBank: handleClickViewContentBank,
        onClickViewGradingSettings: handleClickViewGradingSettings,
        onClickViewSubmissions: handleClickViewSubmissions,
        onClickImportSubmissions: importAPISubmissions,
        onSelect: handleSelect,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    useEffect(() => {
        if (!currentAssignmentId) return;
        if (currentAssignmentId === EXAMPLE_ASSIGNMENT_ID) return setIsFetchingLatestAssignment(false);
        setTimeout(async () => {
            const assignment = await assignmentService.getAssignment(currentAssignmentId);
            setAssignments((current) => {
                if (!current) return current;
                return current.map((a) => {
                    if (a.id !== currentAssignmentId) return a;
                    return resolveFeatureFlags(assignment, department?.features);
                });
            });
            setIsFetchingLatestAssignment(false);
        }, 150);
    }, [currentAssignmentId, department?.features, setAssignments]);

    if (isLoading || currentAssignmentId !== params.id || isFetchingLatestAssignment) return <Loading />;

    if (assignments && !currentAssignment) return <ErrorPage />;

    return (
        <>
            {renderResponsiveLayout(device)}
            {currentAssignment && (
                <>
                    <EditContentBankResourcesDialog
                        readOnly
                        open={viewContentBankResourcesDialog.open}
                        resources={currentAssignment.content_bank_resources || []}
                        onClose={() => setViewContentBankResourcesDialog({ open: false })}
                        onSave={() => {}}
                    />
                    <ViewGradingSettingsDialog
                        open={viewGradingSettingsDialog.open}
                        onClose={() => setViewGradingSettingsDialog({ open: false })}
                    />
                </>
            )}
            <UploadPreviewSubmissionsDialog
                open={uploadPreviewSubmissionsDialog.open}
                onClose={() => setUploadPreviewSubmissionsDialog({ open: false })}
                onClick={attachPreviewFiles}
            />
            <UploadSubmissionsDialog
                open={uploadSubmissionsDialog.open}
                assignment={currentAssignment}
                onClose={() => setUploadSubmissionsDialog({ open: false })}
                onClick={createSubmissions}
            />
            <PushRubricDialog
                open={pushRubricDialog.open}
                form={pushRubricDialog.form}
                onClose={closePushRubricDialog}
                onSave={pushRubricDialog.onSave}
            />
        </>
    );
};

export default PageLayout;
